import { useEffect, useRef, useCallback } from 'react';

const WS_BASE = process.env.NODE_ENV === 'production'
  ? `wss://${window.location.host}/api/v1/ws`
  : 'ws://localhost:8002/api/v1/ws';

/**
 * useWs — connects to a WebSocket channel and calls onMessage on events.
 * Falls back gracefully: if WS fails, caller keeps its polling as safety net.
 *
 * @param {string|null} path  e.g. "/order/abc-123" or "/driver/xyz"
 * @param {function} onMessage  called with parsed JSON data
 * @param {string} [token]  optional auth token
 */
export default function useWs(path, onMessage, token = '') {
  const wsRef     = useRef(null);
  const retryRef  = useRef(null);
  const mountedRef = useRef(true);
  const cbRef     = useRef(onMessage);
  cbRef.current = onMessage;

  const connect = useCallback(() => {
    if (!path || !mountedRef.current) return;
    try {
      const url = `${WS_BASE}${path}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      const ws  = new WebSocket(url);
      wsRef.current = ws;

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          cbRef.current(data);
        } catch { /* ignore malformed */ }
      };

      ws.onopen = () => {
        // Keep-alive ping every 25s
        const ping = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
          else clearInterval(ping);
        }, 25000);
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        // Reconnect with backoff
        retryRef.current = setTimeout(connect, 2000);
      };

      ws.onerror = () => ws.close();
    } catch { /* WS not available */ }
  }, [path, token]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      clearTimeout(retryRef.current);
      wsRef.current?.close();
    };
  }, [connect]);
}
